import React, { useEffect, useRef, useState } from 'react';
import ContentEditable from '../utils/SafeContentEditable';
import { createBlock } from '../utils/blockUtils';
import { focusEditable } from '../utils/focusUtils';
import { applyInlineMarkdown, handleEditablePaste } from '../utils/pasteUtils';
import {
  getCaretSplit,
  isCaretAtStart,
  isCaretAtEnd,
  isCaretOnFirstLine,
  isCaretOnLastLine,
  stripHtml,
} from '../utils/caretUtils';
import { useMathPills } from '../hooks/useMathPills';
import { useVariableRefresh } from '../hooks/useVariableRefresh';
import { tryDeleteAdjacentAtomic } from '../utils/atomicCaret';

/**
 * HeadingBlock — H1–H4 depending on props.level.
 *
 * Enter at the end starts a plain paragraph below; Enter mid-text splits the
 * heading (the remainder stays a heading of the same level). Backspace at
 * the start (or in an empty heading) demotes it to a paragraph — text is
 * never lost. Arrow keys walk into neighbouring blocks.
 */
export const HeadingBlock = ({
  block,
  onUpdate,
  onInsertAfter,
  onUpdateAndInsert,
  onNavigate,
  onMergeWithNext,
  resolveVariable,
  availableVariables,
  onCreateVariable,
}) => {
  const [content, setContent] = useState(block.content || '');
  const contentEditableRef = useRef(null);
  const contentRef = useRef(content);
  const level = block.props?.level || 2;

  // Sync external tree changes (e.g. a merge writing into this block)
  useEffect(() => {
    if ((block.content || '') !== contentRef.current) {
      contentRef.current = block.content || '';
      setContent(block.content || '');
    }
  }, [block.content]);

  // Variable pills show the CURRENT context's values
  useVariableRefresh({
    block,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    contentEditableRef,
  });

  const { mathMenu } = useMathPills({
    blockId: block.id,
    contentEditableRef,
    contentRef,
    setContent,
    onUpdate,
    resolveVariable,
    availableVariables,
    onCreateVariable,
  });

  const handleChange = (e) => {
    const value = applyInlineMarkdown(e.target.value);
    contentRef.current = value;
    setContent(value);
    onUpdate(block.id, { content: value });
  };

  const handleKeyDown = (e) => {
    const el = contentEditableRef.current;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const split = el ? getCaretSplit(el) : null;
      if (split && stripHtml(split.after).length > 0) {
        // Split mid-heading: the remainder stays a heading of the same level
        contentRef.current = split.before;
        setContent(split.before);
        onUpdateAndInsert(
          block.id,
          { content: split.before },
          createBlock('heading', split.after, { level }),
          { focusAtStart: true }
        );
      } else {
        onInsertAfter?.(block.id, 'text');
      }
      return;
    }

    if (e.key === 'ArrowUp' && el && isCaretOnFirstLine(el)) {
      if (onNavigate?.(block.id, 'up')) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown' && el && isCaretOnLastLine(el)) {
      if (onNavigate?.(block.id, 'down')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowLeft' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtStart(el)
    ) {
      if (onNavigate?.(block.id, 'left')) e.preventDefault();
      return;
    }
    if (
      e.key === 'ArrowRight' &&
      !e.shiftKey &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      el &&
      isCaretAtEnd(el)
    ) {
      if (onNavigate?.(block.id, 'right')) e.preventDefault();
      return;
    }

    if (tryDeleteAdjacentAtomic(e)) {
      if (el) {
        const value = el.innerHTML;
        contentRef.current = value;
        setContent(value);
        onUpdate(block.id, { content: value });
      }
      return;
    }

    if (e.key === 'Backspace') {
      const isEmpty = stripHtml(contentRef.current).trim() === '';
      if (isEmpty || (el && isCaretAtStart(el))) {
        // Demote to a plain paragraph — never delete typed text
        e.preventDefault();
        onUpdate(block.id, { type: 'text', content: contentRef.current || '', props: {} });
        focusEditable(block.id, { atStart: true });
      }
    }

    // Delete at end: pull the next block's text up into this heading
    if (e.key === 'Delete' && el && isCaretAtEnd(el)) {
      const live = el.innerHTML;
      contentRef.current = live;
      setContent(live);
      if (onMergeWithNext?.(block.id, live)) {
        e.preventDefault();
        return;
      }
    }
  };

  return (
    <>
      <ContentEditable
        innerRef={contentEditableRef}
        html={content}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handleEditablePaste}
        className={`heading-block heading-${level} doc-editable`}
        tagName={`h${level}`}
        placeholder={`Heading ${level}`}
      />
      {mathMenu}
    </>
  );
};
